// Build file context string for agent prompts from uploaded files

const TEXT_TYPES = ['text/plain', 'text/markdown', 'text/csv']
const TEXT_EXTENSIONS = ['txt', 'md', 'csv', 'eml']
const MAX_TEXT_LENGTH = 4000

const FILE_DESCRIPTIONS: Record<string, string> = {
  msg: 'Microsoft Outlook email message',
  eml: 'Email message file',
  pdf: 'PDF document',
  docx: 'Microsoft Word document',
  xlsx: 'Microsoft Excel spreadsheet',
  pptx: 'Microsoft PowerPoint presentation',
  png: 'Screenshot/image',
  jpg: 'Photo/image',
  jpeg: 'Photo/image',
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => resolve('')
    reader.readAsText(file)
  })
}

export async function buildFileContext(files: File[]): Promise<string> {
  if (files.length === 0) return ''
  const parts: string[] = ['--- ATTACHED DOCUMENTS ---']

  for (const file of files) {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const isText = TEXT_TYPES.includes(file.type) || TEXT_EXTENSIONS.includes(ext)

    if (isText) {
      const content = await readFileAsText(file)
      const truncated = content.length > MAX_TEXT_LENGTH
        ? content.slice(0, MAX_TEXT_LENGTH) + '\n[... truncated]'
        : content
      parts.push(`\nFile: ${file.name} (${formatSize(file.size)})\nContent:\n${truncated}`)
    } else if (file.type.startsWith('image/')) {
      // Images: create object URL so vision-capable agents can reference the image
      const objectUrl = URL.createObjectURL(file)
      parts.push(
        `\n[ATTACHED IMAGE: ${file.name}]` +
        `\nType: ${FILE_DESCRIPTIONS[ext] || 'Image'} (${formatSize(file.size)})` +
        `\nLocal preview: ${objectUrl}` +
        `\nNote: Image will be OCR-processed server-side. Text content will be extracted and stored.`
      )
    } else {
      const desc = FILE_DESCRIPTIONS[ext] || 'Binary file'
      const emailNote = ext === 'msg'
        ? '\nThis is an email conversation — the filename suggests it contains details about the issue. Please reference the email subject in your analysis.'
        : ''
      parts.push(
        `\nFile: ${file.name} (${formatSize(file.size)}, ${desc})${emailNote}` +
        `\nNote: Content will be extracted server-side. Use the filename for context.`
      )
    }
  }

  parts.push('--- END DOCUMENTS ---')
  return parts.join('\n')
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
